"use strict";
import React, { useCallback, useContext, useLayoutEffect, useRef, useState } from "react";
import { Transition } from "react-transition-group";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import classNames from "classnames";
import { ChevronLeft, ChevronRight, IconSize } from "../../../global/icons";
import { AnimationDuration } from "../../../styles/Animations";
import { ButtonType, ButtonSize } from "../Button/Button";
import IconButton from "../IconButton/IconButton";
import * as styles from "./Carousel-css";
import { spacing } from "../../../tokens/tokens-css";
import { randomString } from "../../../utils";
export const Carousel = ({ itemsPerSlide = 1, buttonsAlwaysShow = true, className, children }) => {
  const listRef = useRef(null);
  const [hasScrolledForward, setHasScrolledForward] = useState(false);
  const [hasMoreToScrollForward, setHasMoreToScrollForward] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [itemCount, setItemCount] = useState(0);
  const items = useRef([]);
  const registerItem = useCallback((id) => {
    items.current = [...items.current, id];
    setItemCount(items.current.length);
  }, []);
  const unregisterItem = useCallback((id) => {
    items.current = items.current.filter((item) => item !== id);
    setItemCount(items.current.length);
  }, []);
  const showButtons = buttonsAlwaysShow || isHovered || isFocused;
  const listPadding = spacing.xs * 2;
  const onScroll = () => {
    if (!listRef.current)
      return;
    const scrollAmount = listRef.current.scrollLeft;
    const scrollSize = listRef.current.scrollWidth;
    const listWidth = listRef.current.clientWidth - listPadding;
    setHasScrolledForward(listRef.current.scrollLeft > listPadding);
    setHasMoreToScrollForward(scrollSize - scrollAmount - listWidth > listPadding);
  };
  const scrollList = (direction) => {
    if (!listRef.current)
      return;
    const distance = listRef.current.clientWidth - listPadding;
    listRef.current.scrollBy(distance * direction, 0);
  };
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      className: classNames(styles.carousel, className),
      onMouseEnter: () => {
        setIsHovered(true);
      },
      onMouseLeave: () => {
        setIsHovered(false);
      }
    },
    /* @__PURE__ */ React.createElement(
      "div",
      {
        className: styles.slider,
        style: assignInlineVars({
          [styles.itemCount]: itemCount.toString(),
          [styles.itemSize]: `${1 / itemsPerSlide * 100}%`
        }),
        ref: listRef,
        onScroll,
        onFocus: () => {
          setIsFocused(true);
        },
        onBlur: () => {
          setIsFocused(false);
        }
      },
      /* @__PURE__ */ React.createElement(carouselContext.Provider, { value: { registerItem, unregisterItem } }, children)
    ),
    /* @__PURE__ */ React.createElement(Transition, { in: hasScrolledForward, mountOnEnter: true, unmountOnExit: true, timeout: AnimationDuration.Short }, (status) => /* @__PURE__ */ React.createElement(
      "div",
      {
        className: styles.buttonContainer,
        onFocus: () => {
          setIsFocused(true);
        },
        onBlur: () => {
          setIsFocused(false);
        }
      },
      /* @__PURE__ */ React.createElement(
        IconButton,
        {
          className: styles.sliderButton({ location: "left", status, visible: showButtons && hasScrolledForward }),
          icon: /* @__PURE__ */ React.createElement(ChevronLeft, { size: IconSize.Small }),
          type: ButtonType.Tertiary,
          size: ButtonSize.XSmall,
          "aria-label": "previous slide",
          onClick: () => {
            scrollList(-1);
          },
          isIconOnly: true
        }
      )
    )),
    /* @__PURE__ */ React.createElement(Transition, { in: hasMoreToScrollForward, mountOnEnter: true, unmountOnExit: true, timeout: AnimationDuration.Short }, (status) => /* @__PURE__ */ React.createElement(
      "div",
      {
        className: styles.buttonContainer,
        onFocus: () => {
          setIsFocused(true);
        },
        onBlur: () => {
          setIsFocused(false);
        }
      },
      /* @__PURE__ */ React.createElement(
        IconButton,
        {
          className: styles.sliderButton({
            location: "right",
            status,
            visible: showButtons && hasMoreToScrollForward
          }),
          "aria-label": "next slide",
          icon: /* @__PURE__ */ React.createElement(ChevronRight, { size: IconSize.Small }),
          type: ButtonType.Tertiary,
          size: ButtonSize.XSmall,
          onClick: () => {
            scrollList(1);
          },
          isIconOnly: true
        }
      )
    ))
  );
};
export const CarouselItem = ({ children, className }) => {
  const { registerItem, unregisterItem } = useCarouselContext();
  const itemId = useRef(randomString());
  useLayoutEffect(() => {
    const id = itemId.current;
    registerItem(id);
    return () => {
      unregisterItem(id);
    };
  }, [registerItem, unregisterItem]);
  return /* @__PURE__ */ React.createElement("div", { className: classNames(styles.item, className) }, children);
};
const carouselContext = React.createContext(void 0);
const useCarouselContext = () => {
  const context = useContext(carouselContext);
  if (!context) {
    throw new Error("useCarouselContext must be used inside a CarouselContext.Provider");
  }
  return context;
};
